import React, { useMemo, useState } from 'react';
import { useStore, uid } from '../state/store';
import { Card, Chip, Banner, Empty, Area, Text, Select, SectionHead, Stat, EvidenceBadge } from '../components/ui';
import { weeklyDigest } from '../lib/coach';
import { composeBriefing } from '../lib/bridge';
import { fmtDate, addDays, weekStart as wkStart } from '../lib/dates';
import { fmtMoney } from '../lib/derive';
import { orgPrimaryDealEnabled, orgReviewConcentrate } from '../lib/orgPlan';

const DEFAULT_CONCENTRATE_FIELDS = [
  { key: 'money', label: 'Money', help: 'Paid, invoiced, committed.' },
  { key: 'asset', label: 'Asset', help: 'Named assets, documents, title information.' },
  { key: 'regulatedExecution', label: 'Regulated execution', help: 'Partner acceptance, permissions, next gates.' },
  { key: 'institutionalAccess', label: 'Institutional access', help: 'Who let you into a real process.' },
  { key: 'capital', label: 'Capital', help: 'Distribution or investor acceptance signals.' },
  { key: 'funding', label: 'Funding', help: 'Runway, co-founder, capital raise.' },
];

const PACKAGE_CONCENTRATE_FIELDS = [
  { key: 'touches', label: 'Personalized touches', help: 'Sends / replies this week.' },
  { key: 'discoveries', label: 'Discoveries', help: 'Problem-confirmed conversations.' },
  { key: 'proposals', label: 'Proposals', help: 'Commercial next steps issued.' },
  { key: 'sowPath', label: 'SOW path', help: 'Signed SOW or deposit progress.' },
  { key: 'funding', label: 'Runway', help: 'Consulting lean runway posture.' },
];

export default function Review() {
  const { state, d, add, patch } = useStore();
  const concentrate = orgReviewConcentrate(state);
  const FIELDS = concentrate?.fields?.length
    ? concentrate.fields.map((f) => ({ key: f.key, label: f.label, help: f.hint || '' }))
    : orgPrimaryDealEnabled(state)
      ? DEFAULT_CONCENTRATE_FIELDS
      : PACKAGE_CONCENTRATE_FIELDS;
  const preSprint = d.sprint.phase === 'pre';
  const digest = useMemo(() => weeklyDigest(state, d), [state, d]);
  const lastCloseout = useMemo(() => {
    const rows = [...(state.closeouts || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return rows[0] || null;
  }, [state.closeouts]);
  const briefing = useMemo(
    () => composeBriefing(state, d, {
      lastCloseout,
      overrides: state.bridgeRecommendations || [],
      includeFriday: !preSprint,
    }),
    [state, d, lastCloseout, preSprint],
  );
  const friday = preSprint ? null : briefing.friday;
  const [weekKey, setWeekKey] = useState(d.sprint.weekStart);

  const existing = (state.reviews || []).find((r) => r.weekStart === weekKey);
  const review = existing || null;

  const startReview = () => {
    if (preSprint) return;
    add('reviews', {
      id: uid('rev'),
      weekStart: weekKey,
      money: '', asset: '', regulatedExecution: '', institutionalAccess: '', capital: '', funding: '',
      notWorking: '', wasted: '', continue: '', stop: '', concentrate: '',
      constraint: state.d001?.constraint?.blocker || '',
      nextObjective: '',
      leak: '', leakNote: '', survives: '', survivesNote: '',
    });
  };

  const upd = (k) => (v) => patch('reviews', existing.id, { [k]: v });
  const weeks = weekOptions(d.sprint.weekStart, d.sprint.start);

  const useBridgeJudgement = () => {
    if (!existing || !friday || preSprint) return;
    patch('reviews', existing.id, {
      continue: friday.continue,
      stop: friday.stop,
      concentrate: friday.concentrate,
      constraint: friday.constraint,
      nextObjective: friday.nextObjective,
    });
    add('recommendationDecisions', {
      id: uid('rd'),
      kind: 'friday_judgement',
      status: 'accepted',
      reason: 'Use Bridge judgement',
      snapshotId: briefing.fingerprint || '',
      scoringConfigVersion: briefing.scoringConfigVersion || '',
      date: d.sprint.today,
      replacementMoveId: '',
      actionId: '',
    });
  };

  if (preSprint) {
    return (
      <>
        <div className="page-head">
          <div className="row">
            <h2>WEEKLY REVIEW</h2>
            <div className="spacer" />
            <Chip>Pre-sprint</Chip>
          </div>
          <div className="sub">Execution has not started yet.</div>
        </div>
        <section className="section">
          <Card title="Review abstains">
            <p className="bridge-copy">
              Weekly review begins after execution starts ({fmtDate(d.sprint.start, { year: true })}).
              Bridge will not manufacture Continue / Stop / Concentrate judgements before Day 1.
            </p>
          </Card>
        </section>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>WEEK OF {fmtDate(weekKey, { year: true }).toUpperCase()}</h2>
          <select value={weekKey} onChange={(e) => setWeekKey(e.target.value)} style={{ width: 220 }}>
            {weeks.map((w) => (
              <option key={w} value={w}>
                Week of {fmtDate(w, { year: true })}
              </option>
            ))}
          </select>
          <div className="spacer" />
          <Chip>{(state.reviews || []).length} review(s) completed</Chip>
        </div>
        <div className="sub">Every Friday. The week is not over until it has been judged.</div>
      </div>

      {weekKey === d.sprint.weekStart && (
        <section className="grid g4">
          <Card><Stat label="Commercial commitments" value={<span className="num">{digest.e4}</span>} /></Card>
          <Card><Stat label="Binding proof" value={<span className="num">{digest.e5}</span>} /></Card>
          <Card><Stat label="Spend this week" value={<span className="num">{fmtMoney(digest.spend, state.settings?.currency)}</span>} /></Card>
          <Card><Stat label="Founder hours" value={<span className="num">{digest.hours}</span>} /></Card>
        </section>
      )}

      {weekKey === d.sprint.weekStart && friday && (
        <section className="section">
          <Card
            title="The Bridge judgement"
            sub="CONTINUE / STOP / CONCENTRATE from recorded state. The founder still owns the saved review."
            right={
              review ? (
                <button className="btn sm primary" onClick={useBridgeJudgement}>
                  Use Bridge judgement
                </button>
              ) : null
            }
          >
            <dl className="kv">
              <dt>Continue</dt>
              <dd>{friday.continue}</dd>
              <dt>Stop</dt>
              <dd>{friday.stop}</dd>
              <dt>Concentrate</dt>
              <dd>{concentrate?.label || friday.concentrate}</dd>
              <dt>Current constraint</dt>
              <dd>{friday.constraint}</dd>
              <dt>Next week’s objective</dt>
              <dd>{concentrate?.nextWeekObjective || friday.nextObjective}</dd>
            </dl>
          </Card>
        </section>
      )}

      {weekKey === d.sprint.weekStart && (
        <section className="section">
          <Card title="Evidence recorded this week" flush>
            {digest.evidence.length === 0 ? (
              <div style={{ padding: 14 }}>
                <Empty title="No evidence recorded this week.">
                  {digest.activities.length} activity record(s) exist. Activity is not the same as proof.
                </Empty>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {digest.evidence.map((e) => (
                      <tr key={e.id}>
                        <td style={{ width: 60 }}><EvidenceBadge level={e.level} /></td>
                        <td>{e.title}<div className="dim tiny">{e.counterparty || 'unnamed'} · {e.market}</div></td>
                        <td className="num" style={{ width: 80 }}>{fmtDate(e.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>
      )}

      {weekKey === d.sprint.weekStart && <FunnelBlock />}

      <section className="section">
        {!review ? (
          <Empty title="This week has not been reviewed.">
            Six evidence questions, three judgements, one constraint, one objective.
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn primary" onClick={startReview}>Start the review</button>
            </div>
          </Empty>
        ) : (
          <>
            <SectionHead title="Evidence gained" hint="Only what a counterparty did." />
            <div className="grid g3">
              {FIELDS.map((f) => (
                <Card key={f.key} title={f.label} sub={f.help}>
                  <Area label="" value={review[f.key]} onChange={upd(f.key)} rows={3} placeholder="None." />
                </Card>
              ))}
            </div>

            <SectionHead title="Judgement" />
            <div className="grid g2">
              <Card title="What did not work?">
                <Area label="" value={review.notWorking} onChange={upd('notWorking')} rows={3} />
              </Card>
              <Card title="What consumed time or cash without producing proof?">
                <Area label="" value={review.wasted} onChange={upd('wasted')} rows={3} />
              </Card>
              <Card title="Continue — what works?">
                <Area label="" value={review.continue} onChange={upd('continue')} rows={3} />
              </Card>
              <Card title="Stop — what should end Monday?">
                <Area label="" value={review.stop} onChange={upd('stop')} rows={3} />
              </Card>
            </div>

            <div className="grid g2" style={{ marginTop: 14 }}>
              <Card title="Concentrate — where should attention double?">
                <Area label="" value={review.concentrate} onChange={upd('concentrate')} rows={3} />
              </Card>
              <Card title="The constraint">
                <Area
                  label="The single thing most limiting company value right now is:"
                  value={review.constraint}
                  onChange={upd('constraint')}
                  rows={3}
                />
              </Card>
            </div>

            <SectionHead title="Funnel judgement" hint="Deal health is not the same as funnel health." />
            <div className="grid g2">
              <Card title="Where is the largest leak?">
                <Select label="" value={review.leak || ''} onChange={upd('leak')} options={['', ...LEAKS]} />
                <Area label="Why do you think so?" value={review.leakNote || ''} onChange={upd('leakNote')} rows={3} />
              </Card>
              <Card title="If Primary D001 disappeared today, do we have a credible next path?">
                <div className="row tight" style={{ marginBottom: 10 }}>
                  {['Yes', 'Probably', 'No'].map((o) => (
                    <button
                      key={o}
                      className={`btn ${review.survives === o ? 'primary' : ''}`}
                      onClick={() => upd('survives')(o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                <Area label="Rationale" value={review.survivesNote || ''} onChange={upd('survivesNote')} rows={3} placeholder="Name the actual next path, or say plainly that there is none." />
                {review.survives === 'No' && (
                  <Banner tone="stop">
                    Concentration risk is the constraint this week, whatever else is on the board.
                  </Banner>
                )}
              </Card>
            </div>

            <section className="section">
              <Card title="Next week's one objective" sub="Only one. Not five.">
                <Text label="" value={review.nextObjective} onChange={upd('nextObjective')} placeholder="One sentence. It should name the evidence it will produce." />
                {countObjectives(review.nextObjective) > 1 && (
                  <Banner tone="warn">That reads like more than one objective. Pick the one that unlocks the others.</Banner>
                )}
              </Card>
            </section>
          </>
        )}
      </section>

      {(state.reviews || []).length > 1 && (
        <section className="section">
          <SectionHead title="Past reviews" />
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>Week</th>
                    <th>Next objective</th>
                    <th>Constraint</th>
                    <th>Stop</th>
                  </tr>
                </thead>
                <tbody>
                  {[...state.reviews].sort((a, b) => (a.weekStart < b.weekStart ? 1 : -1)).map((r) => (
                    <tr key={r.id || r.weekStart}>
                      <td className="num">{fmtDate(r.weekStart, { year: true })}</td>
                      <td>{r.nextObjective || <span className="dim">—</span>}</td>
                      <td>{r.constraint || <span className="dim">—</span>}</td>
                      <td>{r.stop || <span className="dim">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      )}
    </>
  );
}

export const LEAKS = [
  'Target quality',
  'Outreach has not started yet',
  'Access / outreach conversion',
  'Conversation conversion',
  'Engagement',
  'Discovery',
  'Qualification',
  'Asset commitment',
  'Asset quality',
  'Regulated execution',
  'Commercial conversion',
  'Capital',
  'Other',
];

function FunnelBlock() {
  const { d } = useStore();
  const f = d.funnel;
  const rows = [
    ['Market universe', f.counts.universe],
    ['Qualified targets', f.counts.qualifiedTargets],
    ['Active Tier A', f.counts.tierA],
    ['Engaged', f.counts.engaged],
    ['Discovery', f.counts.discovery],
    ['Qualified opportunities', f.counts.qualified],
    ['Assets identified', f.counts.assetsIdentified],
    ['D001 candidates', f.counts.candidates],
    ['Primary D001', f.counts.primary],
    ['Backup D001', f.counts.backups],
  ];
  return (
    <section className="section">
      <SectionHead title="Funnel health" hint={`${f.health.status} · concentration ${f.concentration.level}`} />
      <Card>
        <div className="grid g5">
          {rows.map(([label, value]) => (
            <Stat key={label} label={label} value={<span className="num">{value}</span>} size="sm" />
          ))}
        </div>
        {f.diagnosis.length > 0 && (
          <>
            <div className="hr" />
            <div className="dim tiny" style={{ marginBottom: 5 }}>Diagnosis from your own conversion data</div>
            {f.diagnosis.map((x) => (
              <div key={x.key} style={{ fontSize: 13, marginBottom: 5 }}>
                <strong>{x.stage}:</strong> {x.text}
              </div>
            ))}
          </>
        )}
      </Card>
    </section>
  );
}

function weekOptions(current, sprintStart) {
  const out = [];
  let w = wkStart(sprintStart);
  for (let i = 0; i < 8; i += 1) {
    out.push(w);
    w = addDays(w, 7);
  }
  if (!out.includes(current)) out.push(current);
  return out;
}

function countObjectives(text) {
  const t = String(text || '').trim();
  if (!t) return 0;
  const clauses = t.split(/[;.]|\band\b|\+/i).map((x) => x.trim()).filter((x) => x.length > 12);
  return Math.max(1, clauses.length);
}
